import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";

import Layout from "@/components/Layout";
import LoginPage from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import IndentSheet from "@/pages/IndentSheet";
import PurchaseManagement from "@/pages/PurchaseManagement";
import POPrint from "@/pages/POPrint";
import Store from "@/pages/Store";
import IssueSlipPrint from "@/pages/IssueSlipPrint";
import IndentLog from "@/pages/IndentLog";
import AuditLogs from "@/pages/AuditLogs";
import Settings from "@/pages/Settings";
import InwardLog from "@/pages/InwardLog";
import GRNPrint from "@/pages/GRNPrint";
import PORegister from "@/pages/PORegister";
import CustomerPORegister from "@/pages/CustomerPORegister";
import Reports from "@/pages/Reports";
import InvoicePrint from "@/pages/InvoicePrint";
import ItemMaster from "@/pages/ItemMaster";
import CostCenterMaster from "@/pages/CostCenterMaster";
import RequiredMaster from "@/pages/RequiredMaster";
import DepartmentMaster from "@/pages/DepartmentMaster";
import CategoryMaster from "@/pages/CategoryMaster";
import ProcessMaster from "@/pages/ProcessMaster";
import PurchaseGroupDetail from "@/pages/PurchaseGroupDetail";
import { IntenderMaster, SupplierMaster } from "@/pages/Masters";
import CompanyMaster from "@/pages/CompanyMaster";
import CustomerMaster from "@/pages/CustomerMaster";
import BankMaster from "@/pages/BankMaster";
import MR from "@/pages/MR";
import Account from "@/pages/Account";
import AccountsDashboard from "@/pages/AccountsDashboard";
import BudgetSheet from "@/pages/BudgetSheet";
import { SupplierLedger, CustomerLedger } from "@/pages/AccountsLedgers";
import MasterGuard from "@/components/MasterGuard";
import LevelTwoGuard from "@/components/LevelTwoGuard";
import BudgetGuard from "@/components/BudgetGuard";

function Protected({ children, requireAdmin = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (requireAdmin && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function Shell({ children }) {
  return <Layout>{children}</Layout>;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          <Route path="/po/print/:id" element={<Protected><POPrint /></Protected>} />
          <Route path="/issue-slips/print/:id" element={<Protected><IssueSlipPrint /></Protected>} />
          <Route path="/grn/print/:id" element={<Protected><GRNPrint /></Protected>} />
          <Route path="/sales/invoice/print/:id" element={<Protected><InvoicePrint /></Protected>} />
          {/* Public read-only invoice (target of the QR code on every printed invoice) */}
          <Route path="/p/invoice/:id" element={<InvoicePrint />} />

          <Route path="/" element={<Protected><Shell><LevelTwoGuard title="Dashboard Access" note="Enter the Admin / Level-2 approver password to view live procurement metrics."><Dashboard /></LevelTwoGuard></Shell></Protected>} />
          <Route path="/indents" element={<Protected><Shell><IndentSheet /></Shell></Protected>} />
          <Route path="/purchase" element={<Protected><Shell><PurchaseManagement /></Shell></Protected>} />
          <Route path="/store" element={<Protected><Shell><Store /></Shell></Protected>} />
          <Route path="/po-register" element={<Protected><Shell><LevelTwoGuard title="Sales Management Access" note="Enter the Admin / Level-2 approver password to view sales, dispatch & invoicing."><PORegister /></LevelTwoGuard></Shell></Protected>} />
          <Route path="/customer-po-register" element={<Protected><Shell><CustomerPORegister /></Shell></Protected>} />
          <Route path="/reports" element={<Protected><Shell><LevelTwoGuard title="Reports Access" note="Enter the Admin / Level-2 approver password to view inward reports."><Reports /></LevelTwoGuard></Shell></Protected>} />
          <Route path="/indent-log" element={<Protected><Shell><IndentLog /></Shell></Protected>} />
          <Route path="/audit-logs" element={<Protected><Shell><LevelTwoGuard title="Audit Log Access" note="Enter the Admin / Level-2 approver password to view the audit trail."><AuditLogs /></LevelTwoGuard></Shell></Protected>} />
          <Route path="/masters/intender" element={<Protected><Shell><MasterGuard><IntenderMaster /></MasterGuard></Shell></Protected>} />
          <Route path="/masters/item" element={<Protected><Shell><MasterGuard><ItemMaster /></MasterGuard></Shell></Protected>} />
          <Route path="/masters/supplier" element={<Protected><Shell><MasterGuard><SupplierMaster /></MasterGuard></Shell></Protected>} />
          <Route path="/masters/cost-center" element={<Protected><Shell><MasterGuard><CostCenterMaster /></MasterGuard></Shell></Protected>} />
          <Route path="/masters/required" element={<Protected><Shell><MasterGuard><RequiredMaster /></MasterGuard></Shell></Protected>} />
          <Route path="/masters/department" element={<Protected><Shell><MasterGuard><DepartmentMaster /></MasterGuard></Shell></Protected>} />
          <Route path="/masters/category" element={<Protected><Shell><MasterGuard><CategoryMaster /></MasterGuard></Shell></Protected>} />
          <Route path="/masters/process" element={<Protected><Shell><MasterGuard><ProcessMaster /></MasterGuard></Shell></Protected>} />
          <Route path="/masters/company" element={<Protected><Shell><MasterGuard><CompanyMaster /></MasterGuard></Shell></Protected>} />
          <Route path="/masters/customer" element={<Protected><Shell><MasterGuard><CustomerMaster /></MasterGuard></Shell></Protected>} />
          <Route path="/masters/bank" element={<Protected><Shell><MasterGuard><BankMaster /></MasterGuard></Shell></Protected>} />
          <Route path="/mr" element={<Protected><Shell><LevelTwoGuard title="MR Module Access" note="Enter the Admin / Level-2 approver password to view raw-material analytics."><MR /></LevelTwoGuard></Shell></Protected>} />
          <Route path="/account" element={<Protected><Shell><Account /></Shell></Protected>} />
          <Route path="/accounts-dashboard" element={<Protected><Shell><LevelTwoGuard title="Accounts Dashboard Access" note="Enter the Admin / Level-2 approver password to view accounts payable & receivable."><AccountsDashboard /></LevelTwoGuard></Shell></Protected>} />
          <Route path="/accounts-dashboard/supplier/:name" element={<Protected><Shell><LevelTwoGuard title="Supplier Ledger Access" note="Enter the Admin / Level-2 approver password to view supplier ledger."><SupplierLedger /></LevelTwoGuard></Shell></Protected>} />
          <Route path="/accounts-dashboard/customer/:name" element={<Protected><Shell><LevelTwoGuard title="Customer Ledger Access" note="Enter the Admin / Level-2 approver password to view customer ledger."><CustomerLedger /></LevelTwoGuard></Shell></Protected>} />
          <Route path="/budget-sheet" element={<Protected><Shell><BudgetGuard><BudgetSheet /></BudgetGuard></Shell></Protected>} />
          <Route path="/dashboard/purchase-group/:group" element={<Protected><Shell><PurchaseGroupDetail /></Shell></Protected>} />
          <Route path="/inward-log" element={<Protected requireAdmin><Shell><InwardLog /></Shell></Protected>} />
          <Route path="/settings" element={<Protected><Shell><Settings /></Shell></Protected>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors closeButton position="top-right" />
    </AuthProvider>
  );
}
